class EventTargetPolyfill implements EventTarget {
    protected listeners = new Map<string, Array<EventListenerOrEventListenerObject>>();

    public addEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
        let listeners = this.listeners.get(type);

        if(!listeners) {
            listeners = [];
            this.listeners.set(type, listeners);
        }

        listeners.push(listener);
    }

    public dispatchEvent(event: MessageEvent): boolean {
        const listeners = this.listeners.get(event.type);

        if(!listeners?.length) {
            return true;
        }

        for(const listener of listeners) {
            if(typeof listener === "function") {
                listener(event);
            } else {
                listener.handleEvent(event);
            }
        }

        return true;
    }

    public removeEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
        let listeners = this.listeners.get(type);

        if(!listeners?.length) {
            return;
        }

        for(let i = 0, l = listeners.length; i < l; i++) {
            if(listeners[i] === listener) {
                listeners = [
                    ...listeners.slice(0, i),
                    ...listeners.slice(i + 1),
                ];

                this.listeners.set(type, listeners);

                return;
            }
        }
    }
}

export class WorkerPortPolyfill extends EventTargetPolyfill implements MessagePort {
    protected open: boolean;

    public onmessage: ((this: MessagePort, ev: MessageEvent) => any) | null = null;
    public onmessageerror: ((this: MessagePort, ev: MessageEvent) => any) | null = null;

    constructor() {
        super();

        this.open = false;

        self.onmessage = (e: MessageEvent): void => {
            if(!this.open) {
                return;
            }

            this.dispatchEvent(e);
        };

        self.onmessageerror = (e: MessageEvent): void => {
            if(!this.open) {
                return;
            }

            this.dispatchEvent(e);
        };
    }

    public start(): void {
        this.open = true;
    }

    public close(): void {
        this.open = false;
        this.listeners.clear();
    }

    public postMessage(message: MessageEvent): void {
        if(process.env.NODE_ENV === "test") {
            self.postMessage(message, "*");

            return;
        }

        self.postMessage(message);
    }
}

export class WorkerAgentPortPolyfill {
    protected worker: Worker;
    protected listeners: Array<(event: MessageEvent) => void>;

    constructor(worker: Worker) {
        this.worker = worker;
        this.listeners = [];
    }

    public addEventListener(type: string, callback: (event: MessageEvent) => void): void {
        if(type === "messageerror") {
            // Just ignore
            return;
        }

        if(type !== "message") {
            throw new TypeError("Invalid event type provided");
        }

        this.listeners.push(callback);
    }

    public start(): void {
        this.worker.onmessage = (message: MessageEvent): void => {
            for(let i = 0, l = this.listeners.length; i < l; i++) {
                this.listeners[i](message);
            }
        };
    }

    public postMessage(message: unknown): void {
        this.worker.postMessage(message);
    }
}

export class WorkerPolyfill {
    public port: WorkerAgentPortPolyfill;
    public onmessage: (event: MessageEvent) => void;
    protected worker: Worker;

    constructor(url: string | URL, name: string) {
        const options: WorkerOptions = {
            name,
            type: "classic",
        };

        const worker = new Worker(url, options);

        if(typeof worker === "undefined") {
            throw new Error("Agent was constructed with invalid worker given");
        }

        this.worker = worker;
        this.port = new WorkerAgentPortPolyfill(worker);
        this.onmessage = (): void => {
            // No-op
        };
    }

    public terminate(): void {
        this.worker.terminate();
    }
}
